import React, { useState } from 'react';
import { CheckCircle2, Circle, ListChecks, StickyNote, Plus, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const useApi = (token, derivacionId, onUpdated) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const call = async (path, method, body) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/api/derivaciones/${derivacionId}${path}`, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Error al guardar');
      onUpdated();
      return data;
    } catch (err) { setError(err.message); return null; }
    finally { setBusy(false); }
  };
  return { call, busy, error };
};

const AccionItem = ({ a, onToggle, busy }) => {
  const done = a.estado === 'realizada';
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${done ? 'bg-green-50/50 border-green-100' : 'bg-white border-gray-200'}`} data-testid={`accion-${a.id}`}>
      <button type="button" disabled={busy} onClick={() => onToggle(a)} className="mt-0.5" data-testid={`accion-toggle-${a.id}`}
        title={done ? 'Marcar como pendiente' : 'Marcar como realizada'}>
        {done ? <CheckCircle2 size={18} className="text-green-600" /> : <Circle size={18} className="text-gray-300 hover:text-[#B8975A]" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${done ? 'line-through text-gray-400' : 'text-[#1C1C1A]'}`}>{a.descripcion}</p>
        <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3">
          <span>Responsable: <strong className="text-gray-600">{a.responsable_nombre}</strong></span>
          <span>Creada por {a.creada_por} · {fmt(a.fecha_creacion)}</span>
          {done && <span className="text-green-700">Realizada por {a.realizada_por} · {fmt(a.fecha_realizada)}</span>}
        </div>
      </div>
      {done && <RotateCcw size={12} className="text-gray-300 mt-1" />}
    </div>
  );
};

export const AccionesPanel = ({ derivacion, token, onUpdated, isAdmin }) => {
  const [descripcion, setDescripcion] = useState('');
  const [responsable, setResponsable] = useState(isAdmin ? 'clinica' : 'doctor');
  const [aviso, setAviso] = useState('');
  const { call, busy, error } = useApi(token, derivacion.id, onUpdated);
  const acciones = derivacion.acciones || [];
  const pendientes = acciones.filter(a => a.estado === 'pendiente');
  const realizadas = acciones.filter(a => a.estado === 'realizada');

  const crear = async (e) => {
    e.preventDefault();
    if (!descripcion.trim()) return;
    const data = await call('/acciones', 'POST', { descripcion, responsable });
    if (data) {
      setDescripcion('');
      setAviso(data.propia ? 'Acción creada (asignada a usted, sin aviso por email).'
        : data.email_enviado_a ? `Aviso enviado por email a ${data.email_enviado_a}`
        : responsable === 'clinica' ? 'Acción creada. La clínica no tiene email en su perfil: no se envió aviso.'
        : 'Acción creada. No se pudo enviar el aviso por email.');
    }
  };
  const toggle = (a) => call(`/acciones/${a.id}`, 'PATCH', { estado: a.estado === 'realizada' ? 'pendiente' : 'realizada' });

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200" data-testid="acciones-panel">
      <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-3 flex items-center gap-2">
        <ListChecks size={14} />Acciones <span className="normal-case tracking-normal text-[#B8975A]">{pendientes.length} pendientes · {realizadas.length} realizadas</span>
      </h3>
      <form onSubmit={crear} className="flex flex-col md:flex-row gap-2 mb-3">
        <input value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Ej.: Llamar al paciente para confirmar cita"
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="accion-descripcion" />
        <select value={responsable} onChange={e => setResponsable(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="accion-responsable">
          <option value="doctor">Dr. Norro</option>
          <option value="clinica">{derivacion.clinica_nombre || 'Clínica'}</option>
        </select>
        <Button type="submit" size="sm" disabled={busy || !descripcion.trim()} className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white" data-testid="accion-crear">
          <Plus size={14} className="mr-1" />Añadir
        </Button>
      </form>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {aviso && <p className="text-xs text-gray-500 mb-2" data-testid="accion-aviso">{aviso}</p>}
      <div className="space-y-2">
        {[...pendientes, ...realizadas].map(a => <AccionItem key={a.id} a={a} onToggle={toggle} busy={busy} />)}
        {acciones.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Sin acciones todavía</p>}
      </div>
    </div>
  );
};

export const NotasPanel = ({ derivacion, token, onUpdated }) => {
  const [texto, setTexto] = useState('');
  const { call, busy, error } = useApi(token, derivacion.id, onUpdated);
  const notas = [...(derivacion.notas || [])].reverse();

  const crear = async (e) => {
    e.preventDefault();
    if (!texto.trim()) return;
    if (await call('/notas', 'POST', { texto })) setTexto('');
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200" data-testid="notas-panel">
      <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-3 flex items-center gap-2">
        <StickyNote size={14} />Notas compartidas <span className="normal-case tracking-normal text-[11px] bg-[#F0EDE7] text-gray-700 px-1.5 py-0.5 rounded">Visibles para el doctor y la clínica</span>
      </h3>
      <form onSubmit={crear} className="mb-3">
        <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={2} placeholder="Escriba una nota (se guarda con fecha y autor)..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="nota-texto" />
        <Button type="submit" size="sm" disabled={busy || !texto.trim()} className="mt-2 bg-[#1C1C1A] hover:bg-black text-white" data-testid="nota-crear">
          <Plus size={14} className="mr-1" />Añadir nota
        </Button>
      </form>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {notas.map(n => (
          <div key={n.id} className="bg-[#FDFBF8] p-3 rounded-lg text-sm" data-testid={`nota-${n.id}`}>
            <div className="flex justify-between text-xs text-gray-400 mb-1"><strong className="text-gray-600">{n.autor}</strong><span>{fmt(n.fecha)}</span></div>
            <p className="text-[#1C1C1A] whitespace-pre-wrap">{n.texto}</p>
          </div>
        ))}
        {notas.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Sin notas todavía</p>}
      </div>
    </div>
  );
};
